










































































































import { computed, defineComponent, PropType, ref, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { ContentSet, InputContentSetUpdate } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { getElement } from "@/lib/elementHelpers";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { PostFileData } from "@/types";
import BaseSpinner from "@/components/BaseSpinner";
import BaseStepper from "@/components/BaseStepper";
import ContentFeedPreview from "@/components/ContentFeedPreview";
import ModalView from "@/components/ModalView";
import AllowCommentsToggle from "../../components/AllowCommentsToggle";
import AutoPostingToXToggle from "../../components/AutoPostingToXToggle";
import InputWithMentions from "../../components/InputWithMetions";
import PostDraggableView from "../../components/PostDraggableView";
import PostFilesArea from "../../components/PostFilesArea";
import PostHashtagsSettings from "../../components/PostHashtagsSettings";
import PostPriceSettings from "../../components/PostPriceSettings";
import PostPublishConfirm from "../../components/PostPublishConfirm";
import SafeForWorkToggle from "../../components/SafeForWorkToggle";
import SchedulePostSettings from "../../components/SchedulePostSettings";
import StepperButtons from "../../components/StepperButtons";
import useStepperData from "../../hooks/useStepperData";
import { FileUploadData, PostPriceData } from "../../types";

export default defineComponent({
  name: "MediaPostCreateView",
  components: {
    AllowCommentsToggle,
    StepperButtons,
    ContentFeedPreview,
    PostDraggableView,
    PostHashtagsSettings,
    SchedulePostSettings,
    PostPublishConfirm,
    PostPriceSettings,
    PostFilesArea,
    AutoPostingToXToggle,
    SafeForWorkToggle,
    BaseStepper,
    InputWithMentions,
    BaseSpinner,
    ModalView,
  },
  props: {
    show: { type: Boolean, default: false },
    loading: { type: Boolean, required: true },
    saving: { type: Boolean, default: false },
    postData: { type: Object as PropType<ContentSet | null> },
    fileList: { type: Array as PropType<PostFileData[]>, required: true },
    previewFiles: { type: Array as PropType<Array<ContentFile | FileUploadData>>, required: true },
    loadingUploadUrls: { type: Boolean, default: false },
    isDraft: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const stepperEl = ref();
    const showReorder = ref(false);
    const recurringLevel = ref<SubscriptionLevel | null>(null);
    const oneTimePrice = ref<PaymentPackage | null>(null);
    const showPublishConfirm = ref(false);
    const description = ref(props.postData?.description ?? "");
    const title = computed(() => (props.isDraft ? $t("New post") : $t("Edit post")));
    const hashtagsAvailable = computed(() => config.featureHashtags);
    const allowManageReviews = computed(() => config.featureManageFeedReviews);
    const allowManageSchedule = computed(() => props.isDraft || !props.postData?.publishTime);
    const showSecondStep = computed(
      () => allowManageSchedule.value || hashtagsAvailable.value || allowManageReviews.value
    );

    const isPaidPost = computed(
      () => (recurringLevel.value?.pricePackage?.amount ?? oneTimePrice.value?.amount ?? 0) > 0
    );
    const priceData = computed<PostPriceData>(() => ({
      subscriptionLevelId: recurringLevel.value?.id ?? "0",
      pricePackageId: oneTimePrice.value?.id ?? "0",
    }));

    const stepsArray = computed(() => {
      return ["filesAndPrice", ...(showSecondStep.value ? ["settings"] : []), "preview"];
    });

    const { currentStep, steps, getStepName } = useStepperData(stepsArray);

    const onPriceChanged = ({
      recurringLevel: updatedRecurringLevel,
      oneTimePackage,
    }: {
      recurringLevel: SubscriptionLevel | null;
      oneTimePackage: PaymentPackage | null;
    }) => {
      recurringLevel.value = updatedRecurringLevel;
      oneTimePrice.value = oneTimePackage;
    };

    const onPublish = () => {
      const data: Omit<InputContentSetUpdate, "id"> = {
        description: description.value,
        published: true,
        allowReviews: allowManageReviews.value ? props.postData?.allowReviews : undefined,
        tagIds: props.postData?.tagIds ?? [],
      };
      emit("update", { data, closeOnSave: true });
    };

    const onDeclinePublish = () => {
      showPublishConfirm.value = false;
    };

    const handlePublish = () => {
      const isFreeFileInPost = props.fileList.some(file => file.public);
      if (isPaidPost.value && isFreeFileInPost) {
        showPublishConfirm.value = true;
      } else {
        onPublish();
      }
    };

    const onReorderFiles = (ids: string[]) => {
      emit("draggable:update-list", ids);
      showReorder.value = false;
    };

    const onCurrentStepChange = () => {
      getElement<HTMLElement>(stepperEl.value).scrollIntoView({
        block: "start",
        behavior: "auto",
      });
    };

    watch(
      description,
      debounce((description: string) => {
        const data: Omit<InputContentSetUpdate, "id"> = {
          description,
        };
        emit("partialUpdate", { data });
      }, 500)
    );

    return {
      stepperEl,
      title,
      showReorder,
      description,
      showPublishConfirm,
      steps,
      isPaidPost,
      hashtagsAvailable,
      allowManageReviews,
      allowManageSchedule,
      showSecondStep,
      priceData,
      ContentFeedTypeEnum,
      currentStep,
      onCurrentStepChange,
      getStepName,
      onPriceChanged,
      onPublish,
      onDeclinePublish,
      handlePublish,
      onReorderFiles,
    };
  },
});
